import Seo from "@/components/feature/Seo";
import Layout from "@/components/feature/Layout";
import SectionHeader from "@/components/base/SectionHeader";
import { todayUpdates, getComicById } from "@/mocks/comics";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

const filters = [
  { key: "all", label: "全部更新" },
  { key: "连载中", label: "连载中" },
  { key: "已完结", label: "已完结" },
  { key: "新上架", label: "新上架" },
] as const;

export default function Updates() {
  const navigate = useNavigate();
  const [active, setActive] = useState<(typeof filters)[number]["key"]>("all");

  const list = todayUpdates.filter((u) => {
    const comic = getComicById(u.comicId);
    if (!comic) return false;
    return active === "all" || comic.status === active;
  });

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "笨狗漫画网今日更新列表",
    description: "笨狗漫画网每日更新列表，最新漫画、连载、完结作品更新动态一览。",
    url: "https://manhwanalysis.github.io/updates",
    inLanguage: "zh-CN",
    mainEntity: {
      "@type": "ItemList",
      itemListElement: todayUpdates.slice(0, 10).map((u, i) => {
        const comic = getComicById(u.comicId);
        return {
          "@type": "ListItem",
          position: i + 1,
          name: comic ? `${comic.title} ${u.chapterTitle}` : u.chapterTitle,
        };
      }),
    },
  };

  return (
    <Layout>
      <Seo
        title="今日更新列表 | 笨狗漫画网最新漫画连载动态"
        description="笨狗漫画网今日更新列表，最新漫画、连载、完结作品更新动态一目了然，追更不迷路，快速找到你想看的漫画。"
        keywords="笨狗漫画网今日更新,最新漫画,连载漫画,完结漫画,新上架漫画,漫画更新列表"
        canonical="https://manhwanalysis.github.io/updates"
        jsonLd={jsonLd}
      />

      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
          <nav className="flex items-center gap-1 text-sm text-foreground-500">
            <button onClick={() => navigate("/")} className="hover:text-primary-600 cursor-pointer">
              首页
            </button>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-900">今日更新</span>
          </nav>
          <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            今日更新列表
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            今日共更新 <span className="font-bold text-accent-600">{todayUpdates.length}</span>{" "}
            个章节，最新漫画、连载、完结作品动态随时掌握，追更不迷路。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        <div className="mb-5 flex flex-wrap items-center gap-2">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setActive(f.key)}
              className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                active === f.key
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="overflow-hidden rounded-xl border border-background-200 bg-background-50">
          <div className="hidden grid-cols-12 gap-4 border-b border-background-200 bg-background-100 px-5 py-3 text-xs font-semibold text-foreground-600 md:grid">
            <span className="col-span-5">漫画作品</span>
            <span className="col-span-3">更新章节</span>
            <span className="col-span-2">状态</span>
            <span className="col-span-2 text-right">更新时间</span>
          </div>
          <ul>
            {list.map((u) => {
              const comic = getComicById(u.comicId);
              if (!comic) return null;
              return (
                <li
                  key={u.comicId}
                  onClick={() => navigate(`/comic/${comic.id}`)}
                  className="grid grid-cols-12 items-center gap-3 border-b border-background-100 px-4 py-3 transition-colors last:border-0 hover:bg-background-50 md:px-5 cursor-pointer"
                >
                  <div className="col-span-12 flex items-center gap-3 md:col-span-5">
                    <img
                      src={comic.cover}
                      alt={`${comic.title}封面`}
                      className="h-14 w-10 rounded-md object-cover"
                    />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-foreground-950">
                        {comic.title}
                      </p>
                      <p className="truncate text-xs text-foreground-500">{comic.author}</p>
                    </div>
                  </div>
                  <div className="col-span-9 truncate text-sm font-medium text-primary-700 md:col-span-3">
                    {u.chapterTitle}
                  </div>
                  <div className="col-span-3 text-right text-xs text-foreground-500 md:col-span-2 md:text-left">
                    {comic.status}
                  </div>
                  <div className="col-span-12 flex items-center justify-between md:col-span-2 md:justify-end">
                    <span className="rounded-full bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-700">
                      {u.time}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
          {list.length === 0 && (
            <p className="px-6 py-16 text-center text-sm text-foreground-500">
              暂无该状态的作品更新
            </p>
          )}
        </div>
      </section>
    </Layout>
  );
}